import React, { useEffect, useState } from "react";
import { http, fmtErr } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtTRY, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function Admin() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [jewelers, setJewelers] = useState([]);
  const [nj, setNj] = useState({ name: "", city: "İstanbul", contact: "", commission_bps: 500, email: "", password: "" });

  const load = async () => {
    try {
      const [s, e, u, j] = await Promise.all([
        http.get("/admin/stats"), http.get("/admin/events"), http.get("/admin/users"), http.get("/admin/jewelers")
      ]);
      setStats(s.data); setEvents(e.data); setUsers(u.data); setJewelers(j.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const createJeweler = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/jewelers", nj);
      toast.success("Kuyumcu eklendi");
      setNj({ name: "", city: "İstanbul", contact: "", commission_bps: 500, email: "", password: "" });
      load();
    } catch (er) { toast.error(fmtErr(er)); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 space-y-8">
      <div>
        <h1 className="font-serif text-4xl">{t.admin}</h1>
        <p className="text-sm text-muted-foreground">Super admin · Heranida</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          ["Users", stats?.users],
          ["Events", stats?.events],
          ["Paid Gifts", stats?.paid_gifts],
          ["Gross", fmtTRY(stats?.gross_kurus)],
          ["Commission", fmtTRY(stats?.commission_kurus)],
        ].map(([label, val], i) => (
          <Card key={label} className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="font-mono text-xl mt-1" data-testid={`admin-stat-${i}`}>{val ?? "-"}</div>
          </Card>
        ))}
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-serif text-2xl">Kuyumcular</h2>
          <span className="text-xs text-muted-foreground">{t.marketplace_note}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="text-start py-2">Ad</th><th className="text-start">{t.city}</th><th>Komisyon</th><th>{t.orders}</th><th>{t.delivery_rate}</th><th>{t.avg_prep}</th></tr>
            </thead>
            <tbody>
              {jewelers.map(j => (
                <tr key={j.id} className="border-t" data-testid={`admin-jeweler-${j.id}`}>
                  <td className="py-3 font-serif">{j.name}</td>
                  <td>{j.city}</td>
                  <td className="text-center font-mono">{(j.commission_bps || 0) / 100}%</td>
                  <td className="text-center font-mono">{j.orders_total} / {j.orders_delivered}</td>
                  <td className="text-center font-mono">{j.delivery_rate_pct}%</td>
                  <td className="text-center font-mono">{j.avg_prep_minutes ?? "—"} dk</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <form onSubmit={createJeweler} className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 rounded-lg border border-primary/20 bg-muted/20" data-testid="admin-jeweler-form">
          <div><Label>Ad</Label><Input value={nj.name} onChange={(e) => setNj({ ...nj, name: e.target.value })} required data-testid="new-jeweler-name" /></div>
          <div><Label>{t.city}</Label>
            <Select value={nj.city} onValueChange={(v) => setNj({ ...nj, city: v })}>
              <SelectTrigger data-testid="new-jeweler-city"><SelectValue /></SelectTrigger>
              <SelectContent>{["İstanbul", "Ankara", "İzmir", "Bursa", "Antalya"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>İletişim</Label><Input value={nj.contact} onChange={(e) => setNj({ ...nj, contact: e.target.value })} data-testid="new-jeweler-contact" /></div>
          <div><Label>Komisyon (bps)</Label><Input type="number" value={nj.commission_bps} onChange={(e) => setNj({ ...nj, commission_bps: parseInt(e.target.value, 10) })} data-testid="new-jeweler-bps" /></div>
          <div><Label>E-posta</Label><Input type="email" value={nj.email} onChange={(e) => setNj({ ...nj, email: e.target.value })} required data-testid="new-jeweler-email" /></div>
          <div><Label>Parola</Label><Input type="password" minLength={6} value={nj.password} onChange={(e) => setNj({ ...nj, password: e.target.value })} required data-testid="new-jeweler-password" /></div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Button type="submit" className="gap-2 rounded-full" data-testid="new-jeweler-submit"><Plus className="w-4 h-4" /> {t.add_jeweler}</Button>
          </div>
        </form>
      </Card>

      <Card className="p-6">
        <h2 className="font-serif text-2xl mb-4">Etkinlikler</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="text-start py-2">Başlık</th><th className="text-start">Host</th><th>Toplam</th><th>Hediye</th><th>Anahtar</th></tr>
            </thead>
            <tbody>
              {events.map(e => (
                <tr key={e.id} className="border-t" data-testid={`admin-event-${e.id}`}>
                  <td className="py-3 font-serif">{e.title}</td>
                  <td>{e.host_name}</td>
                  <td className="text-center font-mono">{fmtTRY(e.total_cash_kurus)}</td>
                  <td className="text-center font-mono">{e.gift_count}</td>
                  <td className="text-center font-mono text-primary">{e.invite_key}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="font-serif text-2xl mb-4">Kullanıcılar</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="text-start py-2">Ad</th><th className="text-start">E-posta</th><th>Rol</th><th>Kayıt</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t" data-testid={`admin-user-${u.id}`}>
                  <td className="py-3">{u.name}</td>
                  <td>{u.email}</td>
                  <td className="text-center uppercase text-xs tracking-widest">{u.role}</td>
                  <td className="text-center text-muted-foreground text-xs">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
